import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Config } from '@environments/config';
import { HomeTabs } from '@local/client-contracts';
import { isPostWidget } from '@local/common-web';
import { PopupRef, PopupService, UGalleriaComponent, UGalleriaModel } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuItem, ContextMenuService } from '@shared/components';
import { EventsService } from '@shared/services';
import { AppService } from '@shared/services/app.service';
import { KeyboardService } from '@shared/services/keyboard.service';
import { RouterService } from '@shared/services/router.service';
import { SessionService } from '@shared/services/session.service';
import { StreamContentService } from '@shared/services/stream-content.service';
import { TimerService } from '@shared/services/timer.service';
import { isEqual, uniqueId } from 'lodash';
import { filter } from 'rxjs';
import { AppPopupComponent, AppPopupData } from 'src/app/bar/components/app-popup/app-popup.component';
import { MoveWidgetToTabSelect } from 'src/app/bar/components/selected-item-popup/models/move-widget-to-tab-select';
import { WorkspacesService } from 'src/app/bar/services';
import { AvatarListService } from 'src/app/bar/services/avatar-list.service';
import { BlobsService } from 'src/app/bar/services/blobs/blob.service';
import { CollectionsService } from 'src/app/bar/services/collections.service';
import { HomeTabsService } from 'src/app/bar/services/home-tabs.service';
import { CreatePostPopupData } from 'src/app/bar/views/home-page/components/create-post-popup/create-post-popup-data';
import { PostWidgetService } from 'src/app/bar/views/home-page/services/post-widget.service';
import { Published, SETTINGS_WIDGET, WidgetsService } from '../../services/widgets.service';
import { WidgetBaseComponent } from '../widget-base.component';

@UntilDestroy()
@Component({
  selector: 'post-widget',
  templateUrl: './post-widget.component.html',
  styleUrls: ['./post-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PostWidgetComponent extends WidgetBaseComponent<HomeTabs.PostWidget> implements OnDestroy {
  @ViewChild(UGalleriaComponent) carousel: UGalleriaComponent;

  @HostListener('contextmenu', ['$event'])
  onRightClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.activeContextMenu?.destroy();
    if (this.canEdit) {
      this.openContextMenu(event);
    }
  }

  private readonly CAROUSEL_IMAGE_WIDTH = 1280;
  private warningDeletePostPopupRef: PopupRef<AppPopupComponent, AppPopupData>;
  private isCarouselPlaying: boolean;
  private init: boolean;
  private timerId: number;
  private innerAutoPlay: boolean;
  private _autoPlay: boolean;

  showNavigation: boolean;
  carouselData: UGalleriaModel[] = [];

  get defaultAutoplayInterval() {
    return Config.createPostWidget.frameDurationMilliseconds;
  }

  get autoPlay(): boolean {
    return this._autoPlay;
  }
  set autoPlay(a: boolean) {
    this._autoPlay = this.innerAutoPlay = a;
  }

  constructor(
    protected elementRef: ElementRef,
    protected cdr: ChangeDetectorRef,
    private appService: AppService,
    protected keyboardService: KeyboardService,
    protected widgetsService: WidgetsService,
    protected ngZone: NgZone,
    protected session: SessionService,
    protected contextMenuService: ContextMenuService,
    protected workspacesService: WorkspacesService,
    protected popupService: PopupService,
    protected eventsService: EventsService,
    protected routerService: RouterService,
    protected homeTabsService: HomeTabsService,
    protected avatarListService: AvatarListService,
    private blobsService: BlobsService,
    private postWidgetService: PostWidgetService,
    private streamContentService: StreamContentService,
    private collectionsService: CollectionsService,
    private timer: TimerService
  ) {
    super(
      elementRef,
      cdr,
      keyboardService,
      widgetsService,
      ngZone,
      session,
      contextMenuService,
      workspacesService,
      popupService,
      eventsService,
      routerService,
      homeTabsService,
      avatarListService
    );
  }

  loadData(): void {
    this.isLoading = true;
    this.initAutoplay();
    this.initCarousel();
    this.cdr.markForCheck();
  }

  ngOnInit() {
    super.ngOnInit();
    this.widgetsService.published$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x && isPostWidget(x))
      )
      .subscribe((data: Published) => {
        if (isPostWidget(data)) {
          if (data.id && data.id === this.settings.id) {
            this.settings.viewSetting = {
              ...this.settings.viewSetting,
              cols: data.viewSetting.cols,
            };
            if (!isEqual(data.post, this.settings.post)) {
              this.settings.post = data.post;
              this.loadData();
            }
          }
        }
      });
  }

  ngOnDestroy(): void {
    if (this.timerId) {
      this.timer.unregister(this.timerId);
    }
  }

  private initAutoplay() {
    this.autoPlay = this.settings.post?.carouselAutoplay;
    this.isCarouselPlaying = this.autoPlay;
    this.checkButtonsVisibility();
  }

  async initCarousel() {
    if (this.init) {
      return;
    }
    this.init = true;
    const carouselFromCache = this.postWidgetService.getCarouselFromCache(this.settings.id);
    let tempCarouselItems: UGalleriaModel[] = [];
    if (!carouselFromCache) {
      for (const carouselItem of this.settings.post?.carousel ?? []) {
        const thumbnailUrl = this.blobsService.getThumbnailUrl(carouselItem.imageId, this.CAROUSEL_IMAGE_WIDTH);
        tempCarouselItems.push({
          id: +uniqueId(),
          link: carouselItem.link,
          title: carouselItem.imageName,
          imageSrc: thumbnailUrl,
        });
      }
    } else {
      tempCarouselItems = carouselFromCache.carousel;
    }
    this.carouselData = tempCarouselItems;
    await this.refreshBlobsUrls();
    this.postWidgetService.saveCarouselInCache({ postId: this.settings.id, carousel: this.carouselData });
    this.isLoading = false;
    this.init = false;
    this.timerId = this.timer.register(
      async () => {
        // make sure the blob url's ticket is always updated
        await this.refreshBlobsUrls();
      },
      { hiddenInterval: 60 * 1000 }
    );
    this.appService.visible$.pipe(untilDestroyed(this)).subscribe((v) => {
      if (!this.innerAutoPlay) {
        return;
      }
      this._autoPlay = v;
      this.cdr.markForCheck();
    });
    this.cdr.markForCheck();
  }

  private async refreshBlobsUrls() {
    for (const item of this.carouselData) {
      const imageSrc = await this.blobsService.getCdnUrl(item.imageSrc);
      if (item.imageSrc !== imageSrc) {
        item.imageSrc = imageSrc;
      }
    }
  }

  async getAsBlob(thumbnailUrl: string) {
    const url = await this.blobsService.getCdnUrl(thumbnailUrl);
    return await this.streamContentService.streamBlobSrc(url);
  }

  onClick(item: UGalleriaModel) {
    if (item.link) {
      window.open(item.link, '_blank');
    }
  }

  async openContextMenu(event: { x: number; y: number }) {
    const items = this.buildContextMenuItems();
    this.contextMenuOpened = true;
    this.activeContextMenu = this.contextMenuService.open(
      event,
      {
        items,
        onInvoke: (item: ContextMenuItem) => this.handleContextMenuCommand(item),
      },
      { position: 'below' }
    );
    this.cdr.markForCheck();
    this.activeContextMenu.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
      this.contextMenuOpened = false;
      this.cdr.markForCheck();
    });
  }

  buildContextMenuItems(): ContextMenuItem[] {
    let items = [
      {
        id: 'edit',
        text: 'Edit',
        icon: { type: 'font-icon', value: 'icon-edit' },
      },
      {
        id: 'auto-play',
        text: `${this.isCarouselPlaying ? 'Stop' : 'Start'} auto-play`,
        icon: { type: 'font-icon', value: `${this.isCarouselPlaying ? 'icon-pause' : 'icon-play'}` },
      },
      {
        id: 'move-to-tab',
        text: 'Move to Tab',
        icon: { type: 'font-icon', value: 'icon-arrow-right-square' },
      },
      {
        id: 'delete',
        text: 'Delete',
        icon: { type: 'font-icon', value: 'icon-delete' },
      },
    ];
    if (this.settings.post?.carousel?.length <= 1) {
      items = items.filter((item) => item.id !== 'auto-play');
    }
    return items as ContextMenuItem[];
  }

  handleContextMenuCommand(item: ContextMenuItem) {
    switch (item.id) {
      case 'edit':
        const post = this.settings.post;
        this.postWidgetService.openNewPostPopup({
          title: post.title,
          carouselAutoplay: post.carouselAutoplay,
          tab: this.currentTab,
          isEditMode: true,
          carousel: post.carousel,
          currentPostWidget: this.settings,
          isSharedHomePost: this.settings.isShared,
          sizeImage: this.settings.viewSetting.cols === SETTINGS_WIDGET.minCols ? 'small' : 'large',
        } as CreatePostPopupData);
        break;
      case 'auto-play':
        this.settings.post.carouselAutoplay = !this.settings.post?.carouselAutoplay;
        this.autoPlay = this.settings.post?.carouselAutoplay;
        this.isCarouselPlaying = !this.isCarouselPlaying;
        this.checkButtonsVisibility();
        this.postWidgetService.updatePost(this.settings.tabId, this.settings, false, false);
        this.cdr.markForCheck();
        break;
      case 'move-to-tab':
        this.collectionsService.openSelectPopup$.next({
          data: {
            selectedTabId: this.settings.tabId,
            createNewOption: true,
            widget: this.settings,
            itemsList: this.collectionsService.selectedTabs,
          } as MoveWidgetToTabSelect,
          type: 'move-post-widget',
        });
        break;
      case 'delete':
        this.openDeletePostWarningPopup(this.settings);
        break;
      default:
        break;
    }
  }

  checkButtonsVisibility() {
    this.showNavigation = !this.isCarouselPlaying && this.settings.post?.carousel?.length > 1;
    this.cdr.markForCheck();
  }

  openDeletePostWarningPopup(post: HomeTabs.PostWidget) {
    this.warningDeletePostPopupRef?.destroy();
    const popupData: AppPopupData = {
      message: 'Deleting this post will remove it from the homepage of all users who can view it.',
      showButtons: true,
      content: {
        title: 'Are you sure you want to delete this post?',
        secondaryButton: 'Cancel',
        primaryButton: 'Delete',
      },
      messageStyle: { fontSize: '12px', color: 'var(--color-text-secondary)', lineHeight: '18px' },
    };
    this.warningDeletePostPopupRef = this.popupService.open<AppPopupComponent, AppPopupData>('center', AppPopupComponent, popupData, {
      position: 'center',
    });
    this.warningDeletePostPopupRef.compInstance.primaryButton.subscribe(() => {
      this.warningDeletePostPopupRef.destroy();
      this.warningDeletePostPopupRef = null;
      this.postWidgetService.deletePost(post, post.tabId);
    });
    this.warningDeletePostPopupRef.compInstance.secondaryButton.subscribe(() => {
      this.warningDeletePostPopupRef.destroy();
      this.warningDeletePostPopupRef = null;
    });
  }
}
